import { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  useTheme,
} from '@mui/material';
import {
  Remove as RemoveIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  BudgetEditItem,
  BudgetItemOption,
} from '@models/drawRequests/budgetEditItem';
import FormattedCurrency from '@helpers/formattedCurrency';
import { useApp } from '@context/AppContext';
import { colors } from '@theme/colors';

interface BudgetEditCardsProps {
  items: BudgetEditItem[];
  budgetItemOptions: BudgetItemOption[];
  onItemChange: (
    index: number,
    field: keyof BudgetEditItem,
    value: any
  ) => void;
  onRemoveItem: (index: number) => void;
  onAddItem: () => void;
  onValidationChange?: (isValid: boolean) => void;
}

export default function BudgetEditCards({
  items,
  budgetItemOptions,
  onItemChange,
  onRemoveItem,
  onAddItem,
  onValidationChange,
}: BudgetEditCardsProps) {
  const theme = useTheme();
  const { isTabletScreen } = useApp();
  const isFormValid = items.every(
    item =>
      item.budgetItem &&
      item.budgetItem.trim() !== '' &&
      item.requestAmount &&
      item.requestAmount > 0
  );

  const hasExceedingAmounts = items.some(
    item => (item.requestAmount || 0) > (item.remainingAmount || 0)
  );

  const renderBudgetItemField = (item: BudgetEditItem, index: number) => {
    const selectedBudgetItemIds = items
      .map((it, idx) => (idx !== index && it.budgetItem ? it.budgetItem : null))
      .filter((id): id is string => id !== null && id !== '');

    const availableOptions = budgetItemOptions.filter(
      option =>
        !selectedBudgetItemIds.includes(option.value) ||
        option.value === item.budgetItem
    );

    return (
      <Box>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
          }}
          component="label"
          htmlFor={`budget-item-select-${index}`}
        >
          Budget Item
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            id={`budget-item-select-${index}`}
            value={item.budgetItem || ''}
            onChange={e => onItemChange(index, 'budgetItem', e.target.value)}
            displayEmpty
            aria-label={`Select budget item for item ${index + 1}`}
            title={
              budgetItemOptions.find(option => option.value === item.budgetItem)
                ?.label || ''
            }
            sx={{
              backgroundColor: theme.palette.background.paper,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiSelect-select': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            <MenuItem value="" disabled>
              <em
                style={{
                  color: theme.palette.text.disabled,
                  fontStyle: 'italic',
                }}
                aria-hidden="true"
              >
                Select a budget item
              </em>
            </MenuItem>
            {availableOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderRemainingAmountField = (item: BudgetEditItem, index: number) => (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
        }}
        component="label"
        htmlFor={`remaining-amount-${index}`}
      >
        Remaining Amt.
      </Typography>
      <Typography
        variant="body1"
        id={`remaining-amount-${index}`}
        aria-label={`Remaining amount: ${item.remainingAmount}`}
      >
        <FormattedCurrency value={item.remainingAmount} />
      </Typography>
    </Box>
  );

  const renderRequestAmountField = (item: BudgetEditItem, index: number) => {
    const isExceedingRemaining =
      (item.requestAmount || 0) > (item.remainingAmount || 0);

    return (
      <Box>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
          }}
          component="label"
          htmlFor={`request-amount-${index}`}
        >
          Request
        </Typography>
        <TextField
          id={`request-amount-${index}`}
          type="number"
          value={item.requestAmount || ''}
          onChange={e =>
            onItemChange(
              index,
              'requestAmount',
              e.target.value === '' ? '' : parseFloat(e.target.value) || 0
            )
          }
          variant="outlined"
          fullWidth
          size="small"
          aria-label={`Request amount for item ${index + 1}`}
          aria-describedby={
            isExceedingRemaining ? `request-amount-error-${index}` : undefined
          }
          slotProps={{
            input: {
              startAdornment: (
                <Typography variant="body1" sx={{ mr: 1 }} aria-hidden="true">
                  $
                </Typography>
              ),
              endAdornment: isExceedingRemaining ? (
                <InfoIcon
                  sx={{ fill: colors.orange[500], fontSize: '18px' }}
                  aria-hidden="true"
                />
              ) : null,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.background.paper,
              ...(isExceedingRemaining && {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.orange[500],
                  borderWidth: '1px',
                },
              }),
            },
          }}
        />
      </Box>
    );
  };

  const renderPercentCompleteField = (item: BudgetEditItem, index: number) => (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
        }}
        component="label"
        htmlFor={`percent-complete-${index}`}
      >
        % Complete
      </Typography>
      <TextField
        id={`percent-complete-${index}`}
        type="number"
        value={item.percentComplete || ''}
        onChange={e =>
          onItemChange(
            index,
            'percentComplete',
            e.target.value === '' ? '' : parseFloat(e.target.value) || 0
          )
        }
        variant="outlined"
        fullWidth
        size="small"
        aria-label={`Percent complete for item ${index + 1}`}
        slotProps={{
          input: {
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" aria-hidden="true">
                  %
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onItemChange(index, 'percentComplete', 0)}
                  sx={{ p: 0.5 }}
                  aria-label={`Clear percent complete for item ${index + 1}`}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                    aria-hidden="true"
                  >
                    ×
                  </Typography>
                </IconButton>
              </Box>
            ),
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      />
    </Box>
  );

  const renderCardHeader = (index: number) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: theme.palette.primary.main,
        }}
        aria-label={`Budget item ${index + 1}`}
      >
        Item #{index + 1}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => handleRemoveItem(index)}
          disabled={items.length === 1}
          size="small"
          aria-label={`Remove budget item ${index + 1}`}
          sx={{
            color:
              items.length === 1
                ? theme.palette.grey[400]
                : theme.palette.error.main,
            backgroundColor: 'transparent',
            border: `1px solid ${items.length === 1 ? theme.palette.grey[400] : theme.palette.error.main}`,
            borderRadius: '50%',
            width: 24,
            height: 24,
            minWidth: 24,
            '&:hover': {
              backgroundColor:
                items.length === 1
                  ? 'transparent'
                  : theme.palette.error.light + '20',
              borderColor:
                items.length === 1
                  ? theme.palette.grey[400]
                  : theme.palette.error.dark,
            },
            '&:disabled': {
              color: theme.palette.grey[400],
              borderColor: theme.palette.grey[400],
              cursor: 'not-allowed',
            },
            '&:focus': {
              outline: `2px solid ${theme.palette.error.main}`,
              outlineOffset: '2px',
            },
          }}
        >
          <RemoveIcon sx={{ fontSize: '14px' }} aria-hidden="true" />
        </IconButton>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.error.main,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
            '&:focus': {
              outline: `2px solid ${theme.palette.error.main}`,
              outlineOffset: '2px',
            },
          }}
          onClick={() => onRemoveItem(index)}
          role="button"
          tabIndex={0}
          aria-label={`Remove budget item ${index + 1}`}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRemoveItem(index);
            }
          }}
        >
          Remove
        </Typography>
      </Box>
    </Box>
  );

  const renderAddBudgetItemButton = () => (
    <Box sx={{ mt: 1, mb: isTabletScreen ? '120px' : 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          onClick={onAddItem}
          sx={{
            color: theme.palette.primary.main,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
            '&:focus': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
            },
          }}
          role="button"
          tabIndex={0}
          aria-label="Add new budget item"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onAddItem();
            }
          }}
        >
          + Add Budget Item
        </Typography>

        {hasExceedingAmounts && !isTabletScreen && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            role="alert"
            aria-live="polite"
          >
            <InfoIcon
              sx={{ fill: colors.orange[500], fontSize: '18px' }}
              aria-hidden="true"
            />
            <Typography
              variant="body3"
              sx={{
                fontStyle: 'italic',
              }}
            >
              Alert: Budget item(s) exceed the allocated amount.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  useEffect(() => {
    onValidationChange?.(isFormValid);
  }, [isFormValid, onValidationChange]);

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      onRemoveItem(index);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      role="group"
      aria-label="Budget items form"
    >
      {items.map((item, index) => (
        <Card
          key={item.id}
          component="section"
          aria-label={`Budget item ${index + 1}`}
          sx={{
            boxShadow: theme.shadows[2],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {renderCardHeader(index)}

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              role="group"
              aria-label={`Budget item ${index + 1} fields`}
            >
              {renderBudgetItemField(item, index)}
              {renderRemainingAmountField(item, index)}
              {renderRequestAmountField(item, index)}
              {renderPercentCompleteField(item, index)}
            </Box>
          </CardContent>
        </Card>
      ))}

      {renderAddBudgetItemButton()}
    </Box>
  );
}
